import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppBaseComponent } from '@common/components';
import { AsdaBreadcrumbComponent, NavbarComponent, NoticeComponent } from '@shared/components';
import { DEFAULT_HELP_URLS } from '@shared/constants/constants-urls';
import { ConfigKey, IBreadcrumb, IProfile } from '@shared/models';
import { ApiService, BreadcrumbService, LastReportRunsService, ProfileService } from '@shared/services';
import { NoticeContentService } from '@shared/services/notice-content.service';
import { isUndefined } from 'lodash-es';
import { throwError } from 'rxjs';
import { delay, takeUntil, catchError } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, NavbarComponent, NoticeComponent, AsdaBreadcrumbComponent],
})
export class AppComponent extends AppBaseComponent implements OnInit, OnDestroy {
    profile: IProfile;
    helpUrl = '';
    breadcrumbs: IBreadcrumb[] = [];
    defaultPoolingIntervalInMs = 2000;
    noticeContent: string;
    intervalTime = 2000;
    intervalTimeOut: ReturnType<typeof setTimeout>;
    mail: string;
    constructor(
        private breadcrumbService: BreadcrumbService,
        private apiService: ApiService,
        private lastReportRunsService: LastReportRunsService,
        private noticeContentService: NoticeContentService,
        public profileService: ProfileService,
    ) {
        super();
    }

    ngOnInit() {
        if (window.location.pathname !== '/logged-out') {
            this.startLastReportRunPolling();
            this.watchBreadcrumbs();
            this.loadProfile();
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.lastReportRunsService.stopIntervalPooling();
        clearInterval(this.intervalTimeOut);
    }

    loadProfile(): void {
        this.profileService
            .fetchProfile()
            .pipe(
                catchError(() => {
                    return throwError(() => 'Error fetching profile');
                }),
                takeUntil(this.destroyed$),
            )
            .subscribe((profile) => {
                this.profile = profile;
                this.mail = profile.mail;
            });
    }

    startLastReportRunPolling() {
        const polling = () => {
            this.apiService
                .fetchConfig()
                .pipe(takeUntil(this.destroyed$))
                .subscribe((configResponse) => {
                    const configValueTimer = configResponse.find((config) => config.key === ConfigKey.RUNS_REFRESH_TIMER)?.value ?? undefined;
                    const intervalInMs = isUndefined(configValueTimer) ? this.defaultPoolingIntervalInMs : Number(configValueTimer);
                    // Start last report run service pooling
                    this.lastReportRunsService.startIntervalPooling(intervalInMs);
                    // Update other content
                    this.noticeContent = (configResponse.find((config) => config.key === ConfigKey.NOTICE)?.value as string) ?? '';
                    this.noticeContentService.updateContent(this.noticeContent);
                    // Update max file size
                    const maxFileSizeValue = configResponse.find((config) => config.key === ConfigKey.ADHOC_MAX_FILE_SIZE_MB)?.value;
                    if (maxFileSizeValue) {
                        this.noticeContentService.updateMaxFileSize(Number(maxFileSizeValue));
                    }
                    const maxTimeConfigsValue = configResponse.find((config) => config.key === ConfigKey.MAX_TIME_CONFIGS)?.value;
                    if (maxTimeConfigsValue) {
                        this.noticeContentService.updateMaxTimeConfigs(Number(maxTimeConfigsValue));
                    }

                    this.helpUrl = this.mail?.endsWith('@asda.uk')
                        ? ((configResponse.find((config) => config.key === ConfigKey.DSS_HELP_URL_ASDA)?.value as string) ?? DEFAULT_HELP_URLS.ASDA)
                        : ((configResponse.find((config) => config.key === ConfigKey.DSS_HELP_URL_SUPPLIER)?.value as string) ?? DEFAULT_HELP_URLS.SUPPLIER);

                    // Reset interval dynamically
                    clearInterval(this.intervalTimeOut);
                    this.intervalTimeOut = setInterval(polling, intervalInMs);
                });
        };
        // Initial call
        polling();
    }

    watchBreadcrumbs() {
        this.breadcrumbService.breadcrumbs$.pipe(delay(0), takeUntil(this.destroyed$)).subscribe((breadcrumbs) => {
            this.breadcrumbs = breadcrumbs;
        });
    }
}
